<!-- Responsibility: Implement player profile sheet behavior for the ui/components surface.
Scope: Owns ui/components concerns only; cross-surface orchestration is excluded. -->
<player-profile-sheet>
  <div
    class="pmodal-content profile-tab-{ state.profileTab }"
    if={ state.profileModel && state.profileModel.isReady }
    onclick={ (e) => e.stopPropagation() }
    ontouchstart={ onSwipeStart }
    ontouchend={ onSwipeEnd }
  >
    <div class="modal-swipe-handle" aria-hidden="true"></div>

    <player-profile-header
      model={ state.profileModel }
      portraitZoomed={ state.portraitZoomed }
      onClose={ requestClose }
      onTogglePortraitZoom={ togglePortraitZoom }
      onActivateKey={ onActivateKey }
    ></player-profile-header>

    <div class="pmodal-tabs">
      <button class="pmodal-tab { state.profileTab === 'profile' ? 'active' : '' }" onclick={ () => setProfileTab('profile') }>PROFILE</button>
      <button class="pmodal-tab { state.profileTab === 'inventory' ? 'active' : '' }" onclick={ () => setProfileTab('inventory') }>INVENTORY</button>
    </div>

    <div class="pmodal-body">
      <player-profile-vitals
        if={ state.profileTab === 'profile' }
        vitals={ state.profileModel.vitals }
        onOpen={ openVitalInfo }
        onActivateKey={ onActivateKey }
      ></player-profile-vitals>

      <player-profile-attributes if={ state.profileTab === 'profile' } attributes={ state.profileModel.attributes }></player-profile-attributes>

      <player-profile-traits if={ state.profileTab === 'profile' } traits={ state.profileModel.traits }></player-profile-traits>

      <player-profile-list-section
        if={ state.profileTab === 'profile' }
        title="Skills"
        itemLabel="skill"
        prefix="◆"
        entries={ state.profileModel.skills.items }
        overflowCount={ state.profileModel.skills.overflow }
        onOpen={ openSkill }
        onActivateKey={ onActivateKey }
      ></player-profile-list-section>

      <player-profile-list-section
        if={ state.profileTab === 'profile' }
        title="Abilities"
        itemLabel="ability"
        prefix="✦"
        entries={ state.profileModel.abilities.items }
        overflowCount={ state.profileModel.abilities.overflow }
        onOpen={ openAbility }
        onActivateKey={ onActivateKey }
      ></player-profile-list-section>

      <player-profile-effects
        if={ state.profileTab === 'profile' }
        model={ state.profileModel }
        onOpen={ openFxInfo }
        onActivateKey={ onActivateKey }
      ></player-profile-effects>

      <player-profile-inventory if={ state.profileTab === 'inventory' } model={ state.profileModel }></player-profile-inventory>
    </div>
  </div>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { buildInfoDialog } from '../dialog/dialog-openers.ts'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../game-store.js'
    import { buildAbilityModalActions } from '../helpers/ability-actions.ts'
    import { PlayerProfileModel } from '../player-profile/player-profile-model.ts'
    import PlayerProfileHeader from './player-profile-header.riot'
    import PlayerProfileVitals from './player-profile-vitals.riot'
    import PlayerProfileAttributes from './player-profile-attributes.riot'
    import PlayerProfileTraits from './player-profile-traits.riot'
    import PlayerProfileListSection from './player-profile-list-section.riot'
    import PlayerProfileInventory from './player-profile-inventory.riot'
    import PlayerProfileEffects from './player-profile-effects.riot'

    export default {
      components: {
        PlayerProfileHeader,
        PlayerProfileVitals,
        PlayerProfileAttributes,
        PlayerProfileTraits,
        PlayerProfileListSection,
        PlayerProfileInventory,
        PlayerProfileEffects,
      },
      state: {
        profileTab: 'profile',
        portraitZoomed: false,
        profileModel: PlayerProfileModel.empty(),
      },

      onMounted() {
        this.lastOpenToken = Number(this.props.openToken || 0)
        this.update({
          profileTab: this.initialTab(),
          profileModel: PlayerProfileModel.fromGameState(this.currentGameState()),
        })
      },

      onBeforeUpdate(props, state) {
        const token = Number(props.openToken || 0)
        if (token !== this.lastOpenToken) {
          this.lastOpenToken = token
          state.profileTab = this.initialTab(props) === 'inventory' ? 'inventory' : 'profile'
          state.portraitZoomed = false
        }
        state.profileModel = PlayerProfileModel.fromGameState(this.currentGameState(props))
      },

      async requestClose() {
        this.update({ portraitZoomed: false })
        if (typeof this.props.requestModalClose === 'function') await this.props.requestModalClose('close-button')
        else if (typeof this.props.onClose === 'function') this.props.onClose()
      },

      currentGameState(props = this.props) {
        return props.content?.gs ?? props.gs ?? gameStore.state ?? null
      },

      initialTab(props = this.props) {
        const tab = props.content?.initialTab ?? props.initialTab
        return tab === 'inventory' ? 'inventory' : 'profile'
      },

      onActivateKey(e, callback) {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        callback?.()
      },

      setProfileTab(tab) {
        this.update({ profileTab: tab === 'inventory' ? 'inventory' : 'profile' })
      },

      togglePortraitZoom() {
        this.update({ portraitZoomed: !this.state.portraitZoomed })
      },

      onSwipeStart(e) {
        const touch = e?.touches?.[0]
        if (!touch) return
        const body = this.$('.pmodal-body')
        this.swipeEligible = Number(body?.scrollTop ?? 0) <= 6
        if (!this.swipeEligible) return
        this.swipeStartY = touch.clientY
        this.swipeStartX = touch.clientX
      },

      onSwipeEnd(e) {
        if (!this.swipeEligible) return
        this.swipeEligible = false
        const touch = e?.changedTouches?.[0]
        if (!touch) return
        const dy = Number(touch.clientY ?? 0) - Number(this.swipeStartY ?? 0)
        const dx = Math.abs(Number(touch.clientX ?? 0) - Number(this.swipeStartX ?? 0))
        if (dy < 84 || dy <= dx * 1.2) return
        this.requestClose()
      },

      openVitalInfo(vital) {
        if (!vital) return
        dispatcher.trigger('player-profile-sheet', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: String(vital.label || '').replace(/^[^A-Z]+\s*/, ''),
            desc: vital.desc,
            meta: `CURRENT: ${vital.value}`,
          },
        }))
      },

      openAbility(ability) {
        const gs = this.currentGameState()
        if (!ability || !gs) return
        const source = (Array.isArray(gs?.player?.abilities) ? gs.player.abilities : []).find(entry => entry.name === ability.name)
        const resolvedArgs = gameStore.getResolvedAbilityArgs(gs, source)
        const requiresInput = resolvedArgs.length > 0
        const totalMin = (gs.world.clock.day * 24 * 60) + (gs.world.clock.hour * 60) + gs.world.clock.minute
        const cooldown = Number(source?.cooldown ?? 0)
        const lastUsed = Number.isFinite(source?.lastUsed) ? Number(source.lastUsed) : -Infinity
        const cooldownRemaining = Math.max(0, (lastUsed + cooldown) - totalMin)
        const actions = cooldownRemaining > 0
          ? []
          : buildAbilityModalActions(gs, source ?? {
              id: String(ability.name ?? 'ability').toLowerCase().replace(/[^a-z0-9]+/g, '_'),
              name: ability.name,
              desc: ability.desc,
              cooldown,
              args: resolvedArgs,
            }, {
              resolvedArgs,
              targets: [gs.player.identity, ...gs.currentRoom.npcs.map(n => n.name)],
              title: ability.name,
              contextTag: 'profile_ability',
            })

        dispatcher.trigger('player-profile-sheet', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: ability.name,
            desc: [
              ability.desc || 'No description.',
              '',
              `**COOLDOWN**\n${String(ability.meta || '').replace(/^CD\s*/, '')} game-minutes`,
              '',
              requiresInput
                ? '**WHEN USED**\nYou will choose any needed details in a follow-up step.'
                : '**WHEN USED**\nNo extra details required.',
            ].join('\n'),
            meta: cooldownRemaining > 0 ? `READY IN ${cooldownRemaining}m` : 'PLAYER ABILITY',
            actions,
          },
        }))
      },

      openSkill(skill) {
        if (!skill) return
        dispatcher.trigger('player-profile-sheet', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: skill.name.toUpperCase(),
            desc: skill.desc || 'A practical proficiency that improves related action outcomes.',
            meta: `SKILL ${skill.meta}`,
          },
        }))
      },

      openFxInfo(effect, isActive) {
        const label = effect?.label || effect?.text || 'Effect'
        dispatcher.trigger('player-profile-sheet', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: String(label).toUpperCase(),
            desc: isActive ? 'Currently influencing your actions or state.' : 'Currently inactive or suppressed.',
            meta: isActive ? 'ACTIVE EFFECT' : 'DORMANT EFFECT',
          },
        }))
      },
    }
  </script>

  <style>
    player-profile-sheet {
      display: block;
      width: 100%;
    }

    .pmodal-content {
      background:
        linear-gradient(180deg, var(--surface-raised), var(--bg)),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) var(--alpha-subtle), transparent), transparent var(--alpha-ghost));
      border: 1px solid var(--border);
      width: min(96vw, 42.5rem);
      max-width: 42.5rem;
      max-height: min(calc(100dvh - 4rem), 53.75rem);
      overflow-y: hidden;
      overscroll-behavior: contain;
      display: flex;
      flex-direction: column;
      position: relative;
      margin: auto;
      transform: translateY(0.25rem);
      border-radius: 1.125rem;
      box-shadow:
        var(--shadow-elev-3),
        0 0 0 1px color-mix(in srgb, var(--gold) var(--alpha-subtle), transparent),
        inset 0 1px 0 color-mix(in srgb, #f8fbff var(--alpha-subtle), transparent);
      animation: modal-pop-in var(--modal-enter-duration, 0.24s) var(--modal-enter-ease, cubic-bezier(0.22, 1, 0.36, 1));
      isolation: isolate;

      @media (min-width: 56.25rem) {
        width: min(90vw, 57.5rem);
        max-width: 57.5rem;
      }

      @media (min-width: 87.5rem) {
        width: min(84vw, 61.25rem);
        max-width: 61.25rem;
      }

      @media (max-width: 49.9375rem) {
        width: min(100vw, 100%);
        max-width: none;
        max-height: min(100dvh, 100svh);
        border-radius: 0;
      }
    }

    .modal-swipe-handle {
      display: none;
      width: 2.45rem;
      height: var(--space-xs);
      border-radius: 62.4375rem;
      background: var(--text-dim);
      margin: var(--space-xs) auto 0.1rem;
      flex: 0 0 auto;
      opacity: 0.84;
    }

    .pmodal-body {
      padding: 1.6rem var(--space-lg) var(--space-lg);
      display: flex;
      flex-direction: column;
      gap: 2.1rem;
      overflow-y: auto;
      overscroll-behavior: contain;
      animation: profile-section-rise 0.34s ease-out;
    }

    .pmodal-tabs {
      display: flex;
      gap: var(--space-xs);
      padding: 0.92rem var(--space-lg) 0;
      position: sticky;
      top: 0;
      z-index: 3;
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface, #0a0e1a) var(--alpha-high), #09111d), color-mix(in srgb, var(--surface, #0a0e1a) var(--alpha-high), transparent));
      backdrop-filter: blur(0.75rem);
    }

    .pmodal-tab {
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-high), transparent);
      background: color-mix(in srgb, var(--surface-card-2) var(--alpha-ghost), var(--bg));
      color: var(--text-muted);
      font-size: 0.66rem;
      letter-spacing: 0.12em;
      padding: var(--space-xs) 0.88rem;
      border-radius: 62.4375rem;
      cursor: pointer;
      font-family: inherit;
      font-weight: 700;
      transition: transform 0.18s ease, color 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;

      &:hover {
        color: color-mix(in srgb, var(--text-prose) var(--alpha-high), var(--text-prose));
        border-color: color-mix(in srgb, var(--gold) 30%, transparent);
        transform: translateY(-1px);
      }
    }

    .pmodal-tab.active {
      color: var(--gold);
      border-color: color-mix(in srgb, var(--gold) var(--alpha-subtle), transparent);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--gold) var(--alpha-low), #171d25), color-mix(in srgb, var(--gold) var(--alpha-subtle), #07101a));
      box-shadow:
        var(--shadow-elev-1),
        0 0 0 1px color-mix(in srgb, var(--gold) var(--alpha-low), transparent);
      transform: translateY(-1px);
    }

    .profile-tab-inventory .pmodal-body,
    .profile-tab-profile .pmodal-body {
      animation: profile-section-rise 0.28s ease-out;
    }

    @media (max-width: 40.625rem) {
      .modal-swipe-handle { display: block; }

      .pmodal-tabs {
        padding: var(--space-sm) 0.9rem 0;
      }

      .pmodal-body {
        padding: 1.15rem 0.9rem 1.25rem;
        gap: var(--space-lg);
      }
    }
  </style>
</player-profile-sheet>
